/**
 * Italian Language Profile
 *
 * SVO word order, prepositions, space-separated.
 * Features rich verb conjugation with pro-drop (subject omission).
 * Very similar to Spanish but with Italian-specific vocabulary.
 */

import type { LanguageProfile } from './types';

export const italianProfile: LanguageProfile = {
  code: 'it',
  name: 'Italian',
  nativeName: 'Italiano',
  regions: ['western'],
  direction: 'ltr',
  script: 'latin',
  wordOrder: 'SVO',
  markingStrategy: 'preposition',
  usesSpaces: true,
  // Infinitive is standard for Italian software UI (Salvare, Annullare, Aprire)
  // This matches macOS, Windows, and web app conventions
  defaultVerbForm: 'infinitive',
  verb: {
    position: 'start',
    subjectDrop: true,
  },
  references: {
    me: 'io', // "I/me"
    it: 'esso', // "it"
    you: 'tu', // "you"
    result: 'risultato',
    event: 'evento',
    target: 'obiettivo',
    body: 'corpo',
    document: 'documento',
    window: 'finestra',
    detail: 'dettaglio',
  },
  possessive: {
    marker: 'di', // Italian uses "di" for general possession
    markerPosition: 'before-property',
    usePossessiveAdjectives: true,
    specialForms: {
      me: 'mio', // "my" (possessive adjective)
      it: 'suo', // "its"
      you: 'tuo', // "your"
    },
    keywords: {
      // "my" variants (masculine/feminine singular/plural)
      mio: 'me',
      mia: 'me',
      miei: 'me',
      mie: 'me',
      // "your" variants
      tuo: 'you',
      tua: 'you',
      tuoi: 'you',
      tue: 'you',
      // "its/his/her" variants
      suo: 'it',
      sua: 'it',
      suoi: 'it',
      sue: 'it',
    },
  },
  roleMarkers: {
    destination: { primary: 'in', alternatives: ['su', 'a'], position: 'before' },
    source: { primary: 'da', alternatives: ['di'], position: 'before' },
    patient: { primary: '', position: 'before' },
    style: { primary: 'con', position: 'before' },
  },
  keywords: {
    // Class/Attribute operations
    toggle: { primary: 'commutare', alternatives: ['alternare'], normalized: 'toggle' },
    add: { primary: 'aggiungere', alternatives: ['aggiungi'], normalized: 'add' },
    remove: { primary: 'rimuovere', alternatives: ['eliminare', 'togliere'], normalized: 'remove' },
    // Content operations
    put: { primary: 'mettere', alternatives: ['inserire'], normalized: 'put' },
    append: { primary: 'accodare', normalized: 'append' },
    prepend: { primary: 'anteporre', normalized: 'prepend' },
    take: { primary: 'prendere', normalized: 'take' },
    make: { primary: 'fare', alternatives: ['creare'], normalized: 'make' },
    clone: { primary: 'clonare', normalized: 'clone' },
    swap: { primary: 'scambiare', normalized: 'swap' },
    morph: { primary: 'trasformare', alternatives: ['convertire'], normalized: 'morph' },
    // Variable operations
    set: { primary: 'impostare', alternatives: ['definire'], normalized: 'set' },
    get: { primary: 'ottenere', normalized: 'get' },
    increment: { primary: 'incrementare', alternatives: ['aumentare'], normalized: 'increment' },
    decrement: { primary: 'decrementare', alternatives: ['diminuire'], normalized: 'decrement' },
    log: { primary: 'registrare', normalized: 'log' },
    // Visibility
    show: { primary: 'mostrare', alternatives: ['visualizzare'], normalized: 'show' },
    hide: { primary: 'nascondere', normalized: 'hide' },
    transition: { primary: 'transizione', alternatives: ['animare'], normalized: 'transition' },
    // Events
    on: { primary: 'su', alternatives: ['al'], normalized: 'on' },
    trigger: { primary: 'scatenare', alternatives: ['attivare'], normalized: 'trigger' },
    send: { primary: 'inviare', normalized: 'send' },
    // DOM focus
    focus: { primary: 'focalizzare', normalized: 'focus' },
    blur: { primary: 'sfuocare', normalized: 'blur' },
    // Phase 1 (v0.9.90): DOM / form state / debug
    empty: { primary: 'svuotare', alternatives: ['vuoto'], normalized: 'empty' },
    open: { primary: 'aprire', normalized: 'open' },
    close: { primary: 'chiudere', normalized: 'close' },
    select: { primary: 'selezionare', normalized: 'select' },
    clear: { primary: 'cancellare', normalized: 'clear' },
    reset: { primary: 'reimpostare', normalized: 'reset' },
    breakpoint: { primary: 'punto-interruzione', normalized: 'breakpoint' },
    // Navigation
    go: { primary: 'andare', alternatives: ['navigare', 'vai'], normalized: 'go' },
    scroll: { primary: 'scorrere', alternatives: ['scroll'], normalized: 'scroll' },
    push: { primary: 'spingere', alternatives: ['push'], normalized: 'push' },
    replace: { primary: 'sostituire', normalized: 'replace' },
    process: { primary: 'elaborare', alternatives: ['processare'], normalized: 'process' },
    // Async
    wait: { primary: 'aspettare', alternatives: ['attendere'], normalized: 'wait' },
    fetch: { primary: 'recuperare', normalized: 'fetch' },
    settle: { primary: 'stabilizzare', normalized: 'settle' },
    // Control flow
    if: { primary: 'se', normalized: 'if' },
    unless: { primary: 'salvo', normalized: 'unless' },
    when: { primary: 'quando', normalized: 'when' },
    where: { primary: 'dove', normalized: 'where' },
    else: { primary: 'altrimenti', normalized: 'else' },
    repeat: { primary: 'ripetere', normalized: 'repeat' },
    for: { primary: 'per', normalized: 'for' },
    while: { primary: 'mentre', normalized: 'while' },
    // `repeat forever` loop keyword — corpus word recognized so loopType types
    // as `:literal` like EN (the repeat.loopType R1 residue; see spanish.ts).
    forever: { primary: 'forever', normalized: 'forever' },
    continue: { primary: 'continuare', normalized: 'continue' },
    halt: { primary: 'fermare', normalized: 'halt' },
    throw: { primary: 'lanciare', normalized: 'throw' },
    call: { primary: 'chiamare', normalized: 'call' },
    return: { primary: 'ritornare', normalized: 'return' },
    then: { primary: 'allora', alternatives: ['poi', 'quindi'], normalized: 'then' },
    and: { primary: 'e', alternatives: ['anche'], normalized: 'and' },
    // Comparison operator (`target matches .x`). Without this keyword the surface
    // stays an identifier and leaks verbatim into the condition's raw expression,
    // which the core expression parser reads as English (modal-close-backdrop /
    // focus-trap drop their then-branch). Not an ActionType and has no command
    // schema, so no pattern is generated from it.
    matches: { primary: 'corrisponde', normalized: 'matches' },
    // Negative-existence operator (`if no dragHandle set dragHandle to me`). Same
    // seam as `exists`: without the keyword the surface stays an identifier and
    // leaks verbatim into the condition's raw expression (behavior-draggable).
    // Neither an ActionType nor a command schema, so no pattern is generated from it.
    no: { primary: 'nessun', normalized: 'no' },
    end: { primary: 'fine', normalized: 'end' },
    // Advanced
    js: { primary: 'js', normalized: 'js' },
    async: { primary: 'asincrono', normalized: 'async' },
    tell: { primary: 'dire', normalized: 'tell' },
    default: { primary: 'predefinito', normalized: 'default' },
    init: { primary: 'inizializzare', alternatives: ['inizia'], normalized: 'init' },
    behavior: { primary: 'comportamento', normalized: 'behavior' },
    install: { primary: 'installare', normalized: 'install' },
    measure: { primary: 'misurare', normalized: 'measure' },
    beep: { primary: 'beep', normalized: 'beep' },
    break: { primary: 'interrompere', normalized: 'break' },
    copy: { primary: 'copiare', normalized: 'copy' },
    exit: { primary: 'uscire', normalized: 'exit' },
    pick: { primary: 'scegliere', normalized: 'pick' },
    render: { primary: 'renderizzare', normalized: 'render' },
    // Modifiers
    into: { primary: 'in', alternatives: ['dentro'], normalized: 'into' },
    before: { primary: 'prima', normalized: 'before' },
    after: { primary: 'dopo', normalized: 'after' },
    // Common event names (for event handler patterns)
    click: { primary: 'clic', alternatives: ['clicca'], normalized: 'click' },
    // `resize` event (window-resize): dict emits ridimensiona; register it so the
    // event types as literal="resize" (matching en) instead of expression.
    resize: { primary: 'ridimensiona', normalized: 'resize' },
    hover: { primary: 'passaggio', alternatives: ['sorvolo'], normalized: 'hover' },
    submit: { primary: 'invio', alternatives: ['sottomettere'], normalized: 'submit' },
    input: { primary: 'inserimento', alternatives: ['input'], normalized: 'input' },
    change: { primary: 'cambio', alternatives: ['cambiamento'], normalized: 'change' },
    // i18n dict emits `carica` for `load`; without it `on load` events type as expression.
    load: { primary: 'carica', alternatives: ['caricamento'], normalized: 'load' },
    // Event modifiers
    until: { primary: 'fino', normalized: 'until' },
    event: { primary: 'evento', normalized: 'event' },
    from: { primary: 'da', alternatives: ['di'], normalized: 'from' },
    // Phase 8 (htmx v4 localized attributes): attribute-suffix keywords
    // used by the vocab generator — `sse-connettere`, `hx-in-diretta`, etc.
    connect: {
      primary: 'connettere',
      alternatives: ['connettersi', 'collegare'],
      normalized: 'connect',
    },
    stream: { primary: 'trasmettere', alternatives: ['flusso', 'streaming'], normalized: 'stream' },
    live: {
      primary: 'in-diretta',
      alternatives: ['diretta', 'dal-vivo', 'vivo'],
      normalized: 'live',
    },
    socket: { primary: 'socket', alternatives: ['presa', 'websocket'], normalized: 'socket' },
    // Reactive / realtime commands
    bind: { primary: 'vincolare', alternatives: ['legare', 'bind'], normalized: 'bind' },
    intercept: { primary: 'intercettare', alternatives: ['intercept'], normalized: 'intercept' },
    worker: { primary: 'lavoratore', alternatives: ['worker'], normalized: 'worker' },
    eventsource: {
      primary: 'eventsource',
      alternatives: ['sorgente-eventi'],
      normalized: 'eventsource',
    },
  },
  eventHandler: {
    keyword: { primary: 'su', alternatives: ['al'], normalized: 'on' },
    sourceMarker: { primary: 'da', alternatives: ['di'], position: 'before' },
    conditionalKeyword: { primary: 'quando', alternatives: ['se'] },
    // Event marker: al (at/upon), used in SVO pattern
    // Pattern: al [event] [verb] [patient] su [destination?]
    // Example: al clic commutare .active su #button
    // + the eventHandler.keyword word the i18n transformer actually emits —
    // without it every generated fused `<cmd>-event-*-vso` pattern was dead
    // (the swap/if recovery split, #346/#351)
    eventMarker: { primary: 'al', alternatives: ['allo', 'alla', 'su'], position: 'before' },
    temporalMarkers: ['quando', 'al'], // temporal conjunctions (when)
  },
};
